import { PaginatedResponse } from '@carecircle/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getNotificationGroupType, NotificationsGroupType } from './helpers';
import {
  clearNotificationsBadge,
  deleteNotification,
  fetchNotifications,
  readNotification,
} from './actions';
import { Notification } from './model/Notification';
import { NotificationStatus } from './model/NotificationStatus';

export interface NotificationsState {
  notifications: PaginatedResponse<Notification[]>;
  loadingNotifications: boolean;
  loadingReadNotification: boolean;
  isOpen: boolean;
}

const initialState: NotificationsState = {
  notifications: { data: [] },
  loadingNotifications: false,
  loadingReadNotification: false,
  isOpen: false,
};

const removeNotificationById = (state: NotificationsState, id: string) => {
  state.notifications.data = state.notifications.data.filter(
    (notification) => notification.id !== id
  );
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification(state, action: PayloadAction<Notification>) {
      if (
        !state.notifications.data.find(
          (notification) => notification.id === action.payload.id
        )
      ) {
        state.notifications.data.unshift(action.payload);
      }
    },
    deleteNotificationById(state, action: PayloadAction<string>) {
      removeNotificationById(state, action.payload);
    },
    openNotificationsDrawer: (state) => {
      state.isOpen = true;
    },
    closeNotificationsDrawer: (state) => {
      state.isOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loadingNotifications = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        let notifications = state.notifications.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          notifications?.push(...action.payload?.data);
        } else {
          notifications = [...action.payload?.data];
        }
        state.notifications = {
          data: notifications || [],
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingNotifications = false;
      })
      .addCase(readNotification.pending, (state) => {
        state.loadingReadNotification = true;
      })
      .addCase(readNotification.fulfilled, (state) => {
        state.loadingReadNotification = false;
      })
      .addCase(clearNotificationsBadge.pending, (state) => {
        state.loadingReadNotification = true;
      })
      .addCase(clearNotificationsBadge.fulfilled, (state, action) => {
        state.loadingReadNotification = false;
        if (!action.meta.arg) {
          state.notifications.data.forEach((notification) => {
            notification.statusToMember = NotificationStatus.read;
          });
        } else {
          if (action.meta.arg === 'story') {
            state.notifications.data?.forEach((notification) => {
              if (
                NotificationsGroupType.stories ===
                getNotificationGroupType(notification.type)
              ) {
                notification.statusToMember = NotificationStatus.read;
              }
            });
          } else {
            if (action.meta.arg === 'community') {
              state.notifications.data?.forEach((notification) => {
                if (
                  NotificationsGroupType.community ===
                  getNotificationGroupType(notification.type)
                ) {
                  notification.statusToMember = NotificationStatus.read;
                }
              });
            }
          }
        }
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        removeNotificationById(state, action.meta.arg.notificationId);
      });
  },
});

export const {
  addNotification,
  deleteNotificationById,
  openNotificationsDrawer,
  closeNotificationsDrawer,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
